import { CompanyPicker } from '@/components/features/Companies/components/CompanyPicker';
import InputError from '@/components/layout/InputError';
import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
    ResponsiveModalTrigger,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Switch } from '@/components/ui/shadcn/switch';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { createPersonValidationSchema } from '@/lib/validations/person-validation';
import { Company, People } from '@/types';
import { router, useForm } from '@inertiajs/react';
import { BriefcaseBusiness, Edit, Info, Mail, Phone, User } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

const invalidFieldClassName = 'border-destructive ring-destructive/20 ring-[3px]';

interface PersonForm {
    first_name: string;
    last_name: string;
    email: string;
    position?: string;
    phone: string;
    accounting: boolean;
    company_id: string | null;
    [key: string]: string | number | string[] | boolean | null | undefined;
}

interface Props {
    person: People;
    companies: Company[];
    trigger?: React.ReactNode;
}

export default function EditPerson({ person, companies, trigger }: Props) {
    const { t } = useTranslations();
    const { data, setData, put, errors, setError, clearErrors, processing } = useForm<PersonForm>({
        first_name: person.first_name,
        last_name: person.last_name,
        email: person.email,
        phone: person.phone,
        position: person.position || '',
        accounting: person.accounting || false,
        company_id: person.company?.id || null,
    });

    const [isOpen, setIsOpen] = useState(false);

    const validationSchema = useMemo(
        () =>
            createPersonValidationSchema({
                requireCompany: true,
                messages: {
                    first_name_required: t('people.forms.edit.validation.first_name_required', 'First name is required.'),
                    first_name_max: t('people.forms.edit.validation.first_name_max', 'First name must be less than 255 characters.'),
                    last_name_required: t('people.forms.edit.validation.last_name_required', 'Last name is required.'),
                    last_name_max: t('people.forms.edit.validation.last_name_max', 'Last name must be less than 255 characters.'),
                    email_required: t('people.forms.edit.validation.email_required', 'Email is required.'),
                    email_invalid: t('people.forms.edit.validation.email_invalid', 'Please enter a valid email address.'),
                    email_max: t('people.forms.edit.validation.email_max', 'Email must be less than 255 characters.'),
                    position_max: t('people.forms.edit.validation.position_max', 'Position must be less than 255 characters.'),
                    company_required: t('people.forms.edit.validation.company_required', 'Company is required.'),
                },
            }),
        [t],
    );

    const submit = (e: React.FormEvent) => {
        e.preventDefault();
        clearErrors();

        const result = validationSchema.safeParse(data);
        if (!result.success) {
            const seen = new Set<string>();
            result.error.issues.forEach((issue) => {
                const field = issue.path?.[0] !== undefined ? String(issue.path[0]) : '';
                if (!field || seen.has(field)) return;
                seen.add(field);
                setError(field as never, issue.message);
            });
            toast.error(t('people.forms.edit.validation.resolve_errors', 'Please fill in all required fields.'));
            return;
        }

        put(route('person.update', person.id), {
            onSuccess: () => {
                setIsOpen(false);
                router.reload({ only: ['people'] });
            },
            onError: () => {
                // Flash message will be shown by useFlashToasts hook if backend provides one
            },
        });
    };

    return (
        <ResponsiveModal open={isOpen} onOpenChange={setIsOpen} variant="sheet">
            {trigger ? (
                <ResponsiveModalTrigger asChild>{trigger}</ResponsiveModalTrigger>
            ) : (
                <Button variant={'outline'} size="sm" asChild>
                    <ResponsiveModalTrigger>
                        <Edit className="mr-2 h-4 w-4" /> {t('people.forms.edit.buttons.edit', 'Edit')}
                    </ResponsiveModalTrigger>
                </Button>
            )}
            <ResponsiveModalContent size="xl">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>{t('people.forms.edit.title', 'Edit contact person')}</ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {t('people.forms.edit.description', 'Update the information for :name.').replace(
                            ':name',
                            `${person.first_name} ${person.last_name}`,
                        )}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>

                <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
                    <ResponsiveModalBody className="space-y-6">
                        <Card className="rounded-md">
                            <CardHeader className="pb-4">
                                <div className="flex items-start gap-3">
                                    <span className="bg-primary/12 text-primary inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                                        1
                                    </span>
                                    <div className="space-y-1">
                                        <CardTitle>{t('people.forms.edit.sections.company_assignment', 'Company assignment')}</CardTitle>
                                        <CardDescription>
                                            {t('people.forms.edit.sections.company_assignment_desc', 'Select the company this contact belongs to.')}
                                        </CardDescription>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                <Label htmlFor="company_id">
                                    {t('people.forms.edit.labels.company', 'Company')} <span className="text-destructive">*</span>
                                </Label>
                                <CompanyPicker
                                    selectedCompany={
                                        data.company_id
                                            ? {
                                                  id: data.company_id,
                                                  name: companies.find((c) => c.id === data.company_id)?.name || person.company?.name || '',
                                                  company_code:
                                                      companies.find((c) => c.id === data.company_id)?.company_code ||
                                                      person.company?.company_code ||
                                                      '',
                                                  country: companies.find((c) => c.id === data.company_id)?.country || person.company?.country || '',
                                              }
                                            : null
                                    }
                                    onCompanySelect={(company) => {
                                        clearErrors('company_id');
                                        setData('company_id', company?.id || null);
                                    }}
                                    placeholder={t('people.forms.edit.placeholders.select_company', 'Select a company...')}
                                    className={cn(errors.company_id && invalidFieldClassName)}
                                />
                                <InputError className="mt-1" message={errors.company_id} />
                            </CardContent>
                        </Card>

                        <Card className="rounded-md">
                            <CardHeader className="pb-4">
                                <div className="flex items-start gap-3">
                                    <span className="bg-primary/12 text-primary inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                                        2
                                    </span>
                                    <div className="space-y-1">
                                        <CardTitle>{t('people.forms.edit.sections.personal_details', 'Personal details')}</CardTitle>
                                        <CardDescription>
                                            {t('people.forms.edit.sections.personal_details_desc', 'Update the main contact information.')}
                                        </CardDescription>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-5">
                                <div className="grid gap-5 md:grid-cols-2">
                                    <div className="space-y-2">
                                        <Label htmlFor="first_name">
                                            {t('people.forms.edit.labels.first_name', 'First Name')} <span className="text-destructive">*</span>
                                        </Label>
                                        <div className="relative">
                                            <User className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                                            <Input
                                                id="first_name"
                                                className={cn('pl-10', errors.first_name && invalidFieldClassName)}
                                                value={data.first_name}
                                                aria-invalid={!!errors.first_name}
                                                onChange={(e) => {
                                                    clearErrors('first_name');
                                                    setData('first_name', e.target.value);
                                                }}
                                                autoComplete="given-name"
                                                placeholder={t('people.forms.edit.placeholders.first_name', 'First name')}
                                            />
                                        </div>
                                        <InputError message={errors.first_name} />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="last_name">
                                            {t('people.forms.edit.labels.last_name', 'Last Name')} <span className="text-destructive">*</span>
                                        </Label>
                                        <div className="relative">
                                            <User className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                                            <Input
                                                id="last_name"
                                                className={cn('pl-10', errors.last_name && invalidFieldClassName)}
                                                value={data.last_name}
                                                aria-invalid={!!errors.last_name}
                                                onChange={(e) => {
                                                    clearErrors('last_name');
                                                    setData('last_name', e.target.value);
                                                }}
                                                autoComplete="family-name"
                                                placeholder={t('people.forms.edit.placeholders.last_name', 'Last name')}
                                            />
                                        </div>
                                        <InputError message={errors.last_name} />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="email">
                                            {t('people.forms.edit.labels.email', 'Email')} <span className="text-destructive">*</span>
                                        </Label>
                                        <div className="relative">
                                            <Mail className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                                            <Input
                                                id="email"
                                                type="email"
                                                className={cn('pl-10', errors.email && invalidFieldClassName)}
                                                value={data.email}
                                                aria-invalid={!!errors.email}
                                                onChange={(e) => {
                                                    clearErrors('email');
                                                    setData('email', e.target.value);
                                                }}
                                                autoComplete="email"
                                                placeholder={t('people.forms.edit.placeholders.email_address', 'Email address')}
                                            />
                                        </div>
                                        <InputError message={errors.email} />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="phone">{t('people.forms.edit.labels.phone', 'Phone')}</Label>
                                        <div className="relative">
                                            <Phone className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                                            <Input
                                                id="phone"
                                                type="tel"
                                                className={cn('pl-10', errors.phone && invalidFieldClassName)}
                                                value={data.phone}
                                                aria-invalid={!!errors.phone}
                                                onChange={(e) => {
                                                    clearErrors('phone');
                                                    setData('phone', e.target.value);
                                                }}
                                                autoComplete="tel"
                                                placeholder={t('people.forms.edit.placeholders.phone_number', 'Phone number')}
                                            />
                                        </div>
                                        <InputError message={errors.phone} />
                                    </div>

                                    <div className="space-y-2 md:col-span-2">
                                        <Label htmlFor="position">{t('people.forms.edit.labels.position', 'Position')}</Label>
                                        <div className="relative">
                                            <BriefcaseBusiness className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                                            <Input
                                                id="position"
                                                className={cn('pl-10', errors.position && invalidFieldClassName)}
                                                value={data.position}
                                                aria-invalid={!!errors.position}
                                                onChange={(e) => {
                                                    clearErrors('position');
                                                    setData('position', e.target.value);
                                                }}
                                                autoComplete="organization-title"
                                                placeholder={t('people.forms.edit.placeholders.position', 'Position')}
                                            />
                                        </div>
                                        <InputError message={errors.position} />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="rounded-md">
                            <CardHeader className="pb-4">
                                <div className="flex items-start gap-3">
                                    <span className="bg-primary/12 text-primary inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                                        3
                                    </span>
                                    <div className="space-y-1">
                                        <CardTitle>{t('people.forms.edit.sections.accounting_contact', 'Accounting contact')}</CardTitle>
                                        <CardDescription>
                                            {t(
                                                'people.forms.edit.sections.accounting_contact_desc',
                                                'Mark if this person can receive accounting documents.',
                                            )}
                                        </CardDescription>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex items-center space-x-3">
                                    <Switch id="accounting" checked={data.accounting} onCheckedChange={(checked) => setData('accounting', checked)} />
                                    <Label htmlFor="accounting" className="text-sm">
                                        {t('people.forms.edit.labels.accounting_contact', 'Accounting Contact')}
                                    </Label>
                                    <span className="text-muted-foreground text-sm">
                                        {t('people.forms.edit.hints.accounting', 'This person can receive invoices and is from accounting')}
                                    </span>
                                </div>
                                <div className="border-primary/20 bg-primary/5 flex items-center gap-3 rounded-sm border p-4">
                                    <Info className="text-primary h-5 w-5 shrink-0" />
                                    <p className="text-muted-foreground text-sm">
                                        {t(
                                            'people.forms.edit.hints.accounting_info',
                                            'Enable this if the person is responsible for invoice receipt and accounting document access.',
                                        )}
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                    </ResponsiveModalBody>

                    {/* The form's own sticky bar is gone: the sheet footer pins it. */}
                    <ResponsiveModalFooter>
                        <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                            {t('people.forms.edit.buttons.cancel', 'Cancel')}
                        </Button>
                        <Button type="submit" disabled={processing}>
                            {processing
                                ? t('people.forms.edit.buttons.saving', 'Saving...')
                                : t('people.forms.edit.buttons.update_person', 'Update Person')}
                        </Button>
                    </ResponsiveModalFooter>
                </form>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
